import styled from '@emotion/styled'
import { stylesIf } from 'utils/styles'
import { isArray } from 'utils/types'

import {
  getDimensionStyle,
  getFontStyle,
  getInputBorderStyle,
} from 'components/patterns/dimensions'
import Icon from 'components/patterns/Icon'

const withStyle = (Component) =>
  styled(Component)((props) => {
    const {
      size,
      doMargins,
      theme,
      children,
      disabled,
      type,
      onColor,
      icon,
      link,
      round,
      iconProps,
    } = props
    const sizeStyles = getSizeStyles(size)
    const font = getFontStyle(theme)

    if (link || icon) {
      return `
        ${sizeStyles}
        ${font}
        align-items: center;
        border: none;
        background: none;
        margin: 0;
        padding: 0;
        color: ${link ? theme.btnLinkColor : theme.color.slateDisabled};
        cursor: pointer;
        box-shadow: none;
        
        &:hover {
          text-decoration: ${link ? 'underline' : 'none'};
        }

        &:hover,
        &:focus {
          outline: 0;
          color: ${link ? theme.linkHoverColor : theme.btnIconColor}
        }
        ${
          disabled
            ? `
            && {
              color: ${theme.color.slateDisabled};
              &:hover {
                text-decoration: none;
              }
              &:focus {
                color: ${theme.btnIconColor};
              }
            }
            `
            : ''
        }
      `
    }

    // use cases: leading icon, trailing dropdown
    const multipleChildren = isArray(children)
    const dim = getDimensionStyle({ size, doMargins, theme })
    const border = getInputBorderStyle(theme)
    const miscStyles = getMiscStyles(theme)
    const typeStyles = getTypeStyles(
      (disabled && 'disabled') || onColor || type,
      theme,
      iconProps
    )
    const styles = `
        && {
          ${dim}
          ${border}
          ${font}
          ${miscStyles}
          ${sizeStyles}
          ${typeStyles}
          ${multipleChildren ? 'line-height: 1;' : ''}
          ${stylesIf(round, `border-radius: 100%;`)}

          span {
            margin: 0;
          }
          svg {
            margin-bottom: 3px;
          }
        }
        ${multipleChildren ? `&& :last-child {margin-left: 4px;}` : ''}

        .anticon-loading {
          margin-right: 10px;
          top: 2px;
          position: relative;
        }
      `
    return styles
  })

export default withStyle

const getMiscStyles = () => `
  text-align: center;
  &:focus {outline:0;}
`
const getTypeStyles = (type, theme, iconProps) => {
  if (!type) return ''
  const def = {
    primary: `
      color: ${theme.buttonPrimaryColor};
      background-color: ${theme.btnPrimaryBg};
      border-color: ${theme.btnPrimaryBorderColor};

      &:hover {
        background-color: ${theme.btnPrimaryHoverBg};
        border-color: ${theme.btnPrimaryHoverBorderColor};
      }

      &:focus {
        background-color: ${theme.btnPrimaryFocusBg};
        border-color: ${theme.btnPrimaryFocusBorderColor};
      }
    `,
    secondary: `
      color: ${theme.btnSecondaryColor};
      background-color: ${theme.btnSecondaryBg};
      border-color: ${theme.btnSecondaryBorderColor};

      &:hover {
        color: ${theme.btnSecondaryHoverColor};
        border-color: ${theme.btnSecondaryHoverBorderColor};
      }

      &:focus {
        color: ${theme.btnSecondaryFocusColor};
        border-color: ${theme.btnSecondaryFocusBorderColor};
      }
    `,
    tertiary: `
      color: ${theme.btnTertiaryColor};
      background-color: none;
      border-color: none;
      border: none;

      &:hover {
        color: ${theme.btnTertiaryHoverColor};
        border-color: none;
      }
    `,
    danger: `
      color: ${theme.btnDangerColor};
      background-color: ${theme.btnDangerBg};
      border-color: ${theme.btnDangerBorderColor};

      &:hover {
        color: ${theme.btnDangerHoverColor};
        background-color: ${theme.btnDangerHoverBg};
        border-color: ${theme.btnDangerHoverBorderColor};
      }

      &:focus {
        color: ${theme.btnDangerFocusColor};
        background-color: ${theme.btnDangerFocusBg};
        border-color: ${theme.btnDangerFocusBorderColor};
      }
    `,
    icon: `
      && {
        color: ${theme.btnIconColor};
        background-color: transparent;
        border: none;
        width: auto;
        height: auto;
        padding: 5px;
        display: flex;
        
        svg {
          margin-right: 0;
          margin-bottom: 0;
          
          ${stylesIf(
            !iconProps.fill,
            `path {
            fill: ${theme.btnIconColor};
          }`
          )}
        }

        &:hover {
          background-color: ${theme.btnIconHoverBg};
        }
      }
    `,
    light: `
      color: ${theme.btnOnColorLightColor};
      background-color: transparent;
      border-color: ${theme.btnOnColorLightBorderColor};

      &:hover {
        color: ${theme.btnOnColorLightHoverColor};
        border-color: ${theme.btnOnColorLightHoverBorderColor};
      }

      &:focus {
        color: ${theme.btnOnColorLightFocusColor};
        border-color: ${theme.btnOnColorLightFocusBorderColor};
      }
    `,
    dark: `
      color: ${theme.btnOnColorDarkColor};
      background-color: transparent;
      border-color: ${theme.btnOnColorDarkBorderColor};

      &:hover {
        color: ${theme.btnOnColorDarkHoverColor};
        border-color: ${theme.btnOnColorDarkHoverBorderColor};
      }

      &:focus {
        color: ${theme.btnOnColorDarkFocusColor};
        border-color: ${theme.btnOnColorDarkFocusBorderColor};
      }
    `,
    disabled: `
      cursor: not-allowed;
      color: ${theme.btnDisabledColor};
      background-color: ${theme.btnDisabledBg};
      border-color: ${theme.btnDisabledBorderColor};
      text-shadow: none;
      box-shadow: none;

      &:hover {
        color: ${theme.btnDisabledColor};
        background-color: ${theme.btnDisabledBg};
        border-color: ${theme.btnDisabledBorderColor};
      }

      &:focus {
        color: ${theme.btnDisabledColor};
        background-color: ${theme.btnDisabledBg};
        border-color: ${theme.btnDisabledBorderColor};
      }
    `,
  }
  return def[type.toLowerCase()]
}

const sizeStyles = {
  large: `
    padding: 0 12px;
  `,
  medium: `
    line-height: 1;
    padding: 0 10px;
  `,
  default: `
    line-height: 1;
    padding: 0 8px;
    && svg {
      margin-bottom: 2px;
    }
  `,
  small: `
    font-size: 12px;
    line-height: 1.33;
    letter-spacing: 0.3px;
    padding: 0 8px;
    && svg {
      margin-bottom: 2px;
    }
  `,
}

const getSizeStyles = (size = 'default') => sizeStyles[size.toLowerCase()]

export const StyledIcon = styled(Icon)`
  && {
    vertical-align: middle;
    margin-right: 5px;
    ${({ trailing }) => (trailing ? 'margin-left: 5px;' : '')}
  }
`
